<template>
    <pt-dialog-v2
        v-bind="dialogProps"
        custom-class="viewHeatmapDialog"
        @close="closeDialog"
        @confirm="viewHeatmap"
    >
        <div>
            <p :class="$style.info">{{ $t('guide.view_heatmap_dialog_input_title') }}</p>
            <pt-form ref="form" :model="formData" :rules="rules" @submit.prevent>
                <pt-form-item prop="url">
                    <pt-input
                        v-if="!hasAllHeatmap"
                        v-model="heatmapUrl"
                        :class="$style.search"
                        :disabled="submitLoading"
                        @keydown.enter="viewHeatmap"
                    ></pt-input>
                    <pt-autocomplete
                        v-else
                        v-model="formData.url"
                        :class="$style.search"
                        :popperAppendToBody="true"
                        scrollHeight="150px"
                        :placeholder="$t('home.view_heatmap_placeholder')"
                        popperClass="viewHeatmapListPopper"
                        value-key="url"
                        :debounce="1000"
                        selectWhenUnmatched
                        filterable
                        remote
                        :hide-loading="false"
                        :fetch-suggestions="searchPage"
                        @select="handleChange"
                        @keydown.enter="viewHeatmap"
                    >
                    </pt-autocomplete>
                </pt-form-item>
            </pt-form>
            <pt-link :class="$style.heatmapLink" :underline="true" @click="openHeatmapDemo">{{
                $t('guide.view_heatmap_demo_entry')
            }}</pt-link>
        </div>
    </pt-dialog-v2>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import isUrl from 'validator/lib/isURL';
import { useProfileInfo } from '@models/profile/useCurrentProfile';
import { useTopHeatmapList } from '@models/insight/useTopHeatmapList';
import dateUtils from '@utils/date.utils';
import { cloneDeep } from 'lodash-es';
import { useHeatmap } from '@/models/insight/heatmap';
import useEventBus from '@assets/plugins/eventBus';
import { usePtiUsage } from '@models/profile/usePtiUsage';
import { searchTree } from '@utils/common.util';
import { useGuideStore } from '@master/store/guide.store';

// emits
const { eventBus } = useEventBus();
// state
const { t } = useI18n();
const { profileInfo } = useProfileInfo();
const { openOverlayHeatmap } = useHeatmap();
const { topHeatmapList, getTopHeatmapList, getFreeHeatmapUrl, freeHeatmapUrl } =
    useTopHeatmapList();
const topHeatmapListOf20 = ref();
const { trackEvent, openHeatmapDemo } = useGuideStore();
const { ptiModuleUsage } = usePtiUsage();
const form = ref();
const formData = ref({
    url: ''
});

const heatmapUrl = ref(''); // 热图链接
const settedHeatmapUrl = ref(''); // 当前设置的url
const submitLoading = ref(false); // 提交状态

const hasAllHeatmap = computed(() => {
    // 是否热图全开
    return ptiModuleUsage.value?.heatmap === -1;
});
const hasSetedHeatmap = computed(() => {
    // 添加热图状态
    return ptiModuleUsage.value?.heatmap === 1;
});

const validateUrl = (rule: any, value: string, callback: (arg0?: Error | undefined) => void) => {
    const val = !hasAllHeatmap.value ? heatmapUrl.value : value;
    if (!val) {
        callback(new Error(t('error.autocomplete_url_empty')));
    } else if (isUrl(val)) {
        callback();
    } else {
        callback(new Error(t('error.valide_url')));
    }
};

const btnDiabled = computed(() => {
    const url = hasAllHeatmap.value ? formData.value?.url : heatmapUrl.value;
    return !url || !isUrl(url);
});

const dialogProps = computed(() => {
    return {
        title: t('guide.view_heatmap_dialog_title'),
        appendToBody: true,
        modelValue: true,
        confirmText: t('guide.guide_popover_step_4_btn_view'),
        confirmButtonInfo: {
            disabled: btnDiabled.value,
            loading: submitLoading.value
        }
    };
});
const rules = ref({
    url: [
        {
            message: t('error.autocomplete_url_empty'),
            trigger: ['select', 'blur']
        },
        {
            validator: validateUrl,
            trigger: ['select', 'blur']
        }
    ]
});

// 挂载组件
onMounted(() => {
    init();
    eventBus.$on('HeatmapAddSuccess', heatmapAddSuccessCallBack, false);
});

// 挂载组件
onUnmounted(() => {
    eventBus.$off('HeatmapAddSuccess', heatmapAddSuccessCallBack, false);
});

// 初始化
function init() {
    const startTime = dateUtils.getMomentTime({
        num: -6,
        type: 'day',
        isStart: true,
        timezone: profileInfo.value?.timeZone
    } as any);
    const endTime = dateUtils.getMomentTime({
        num: 0,
        type: 'day',
        isStart: true,
        timezone: profileInfo.value?.timeZone
    } as any);
    getTopHeatmapList(
        {
            startTime: startTime,
            endTime: endTime,
            searchAmount: 20,
            urlType: 'original'
        },
        () => {
            topHeatmapListOf20.value = cloneDeep(topHeatmapList.value || []);
            setFreeHeatmapStatus(); // 在回调函数中调用 setFreeHeatmapStatus 方法
        },
        () => {}
    );
}

/**
 * 免费用户：
 *    未设置过：
 *       未进数：
 *          默认填充domain，添加后就保存为热图。
 *       进数：
 *          默认填充一个已有数据的url，添加后就保存为热图。
 *    设置过：显示当前设置中（热图设置）的url，并且不能修改，提示升级。
 * 付费用户：默认为空，出联想下拉
 */
function setFreeHeatmapStatus() {
    if (!profileInfo.value || hasAllHeatmap.value) {
        return;
    }
    if (hasSetedHeatmap.value) {
        getFreeHeatmapUrl(profileInfo.value?.sid, () => {
            heatmapUrl.value = freeHeatmapUrl.value;
            settedHeatmapUrl.value = freeHeatmapUrl.value;
        });
    } else {
        heatmapUrl.value =
            topHeatmapListOf20.value?.[0]?.url || addHttpsPrefix(profileInfo.value?.domain);
        settedHeatmapUrl.value = '';
    }
}

// 打开热图
function viewHeatmap() {
    form.value.validate((valide: boolean) => {
        if (!valide) {
            return false;
        }

        if (hasAllHeatmap.value) {
            openOverlayHeatmap(
                searchTree(topHeatmapList.value, formData.value.url, 'url') || formData.value
            );
            closeDialog();
        } else if (hasSetedHeatmap.value) {
            if (settedHeatmapUrl.value !== heatmapUrl.value) {
                // 如果要打开的url和设置的url 不一致 则更新url后再打开弹出热图
                eventBus.$emit('GuideAddHeatmapPage', {
                    preUrl: settedHeatmapUrl.value,
                    url: heatmapUrl.value
                });
                submitLoading.value = true;
            } else {
                // 否则直接打开弹出热图
                openOverlayHeatmap({ url: heatmapUrl.value });
                closeDialog();
            }
        } else {
            eventBus.$emit('GuideAddHeatmapPage', {
                preUrl: '',
                url: heatmapUrl.value
            });
            submitLoading.value = true;
        }
        trackEvent('hm_add_success', {
            position: 'guide'
        });
    });
}

function addHttpsPrefix(url: string) {
    if (url.startsWith('http://') || url.startsWith('https://')) {
        return url; // 如果URL已经以http或https开头，则不做修改
    }
    return 'https://' + url; // 否则在URL前加上https://前缀
}

function searchPage(query: string, cb: any) {
    getTopHeatmapList(
        {
            searchItem: query,
            searchAmount: 100
        },
        () => {
            cb(topHeatmapList.value || []);
        }
    );
}

function handleChange(val: any) {
    formData.value.url = val.url || val.value;
    form.value.validate();
}

// 关闭弹框
function closeDialog() {
    eventBus.$emit('closeViewHeatmapDialog');
}

// 热图添加成功的回调
function heatmapAddSuccessCallBack() {
    openOverlayHeatmap({ url: heatmapUrl.value });
    submitLoading.value = false;
    closeDialog();
}
</script>

<style lang="scss" module>
@import '@style/import.scss';
:global {
    .viewHeatmapDialog {
        .pt-link {
            display: block;
            position: absolute;
            font-weight: 500;
            margin-top: 30px;
        }
        .pt-autocomplete {
            width: 100%;
        }
    }
}
.info {
    font-size: 13px;
    font-weight: 400;
    line-height: 19px;
    margin-bottom: 8px;
}
</style>
